/**
 * Custom hook for upload and processing in Data Adjustments
 * Handles data processing with chunked upload
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdjustmentsOfDataChunkedUploader from '../services/AdjustmentsOfDataChunkedUploader';
import type { 
  InfoType,
  DataPoint
} from '../types/dataAdjustments.types';
import type {
  AdjustmentsProcessingParams,
  AdjustmentsUploadProgress
} from '../../../types/common';

interface UseAdjustmentsUploadReturn {
  // Processing state
  isProcessing: boolean;
  uploadProgress: AdjustmentsUploadProgress | null;
  uploadError: string | null;
  
  // Processed data
  adjustedData: InfoType[];
  processedDataFrames: Array<unknown>;
  
  // Methods
  processData: (
    params: AdjustmentsProcessingParams,
    uploadIds: { [filename: string]: string },
    fileNames: string[]
  ) => Promise<boolean>;
  resetUpload: () => void;
}

export const useAdjustmentsUpload = (): UseAdjustmentsUploadReturn => {
  const { t } = useTranslation('dataAdjustments');

  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<AdjustmentsUploadProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [adjustedData, setAdjustedData] = useState<InfoType[]>([]);
  const [processedDataFrames, setProcessedDataFrames] = useState<Array<unknown>>([]);

  /**
   * Process data with chunked upload
   */
  const processData = useCallback(async (
    params: AdjustmentsProcessingParams,
    uploadIds: { [filename: string]: string },
    fileNames: string[]
  ): Promise<boolean> => {
    setIsProcessing(true);
    setUploadError(null);
    // Reset progress to prevent showing stale values from previous processing phase
    setUploadProgress(null);

    try {
      // Use optimized chunked processor
      const result = await AdjustmentsOfDataChunkedUploader.processData(
        params,
        uploadIds,
        fileNames,
        (progress: AdjustmentsUploadProgress) => {
          setUploadProgress(progress);
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // If methods are required, return true (don't set data yet)
      if (result.methodsRequired) {
        return true; // Methods required - caller should handle this
      }

      // Set processed data only if processing is complete
      if (result.success && result.data) {
        // Sort info_df to match the fileNames order supplied by the caller so the
        // processed-data table rows appear in the same sequence as the upload table.
        const sortedInfoDf = [...result.data.info_df].sort((a: InfoType, b: InfoType) => {
          const nameA = a['Name der Datei'].replace('_processed', '');
          const nameB = b['Name der Datei'].replace('_processed', '');
          const idxA = fileNames.findIndex((n) => nameA.includes(n.replace('.csv', '')) || n.includes(nameA.replace('.csv', '')));
          const idxB = fileNames.findIndex((n) => nameB.includes(n.replace('.csv', '')) || n.includes(nameB.replace('.csv', '')));
          return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
        });
        const { dataframe } = result.data;
        const info_df: InfoType[] = sortedInfoDf;

        // Create map of filename to measurement type
        const fileToMeasurement = new Map(
          info_df.map((info: InfoType) => [
            info["Name der Datei"],
            info["Name der Messreihe"],
          ])
        );

        // Define type for data points
        type DataSeriesPoint = {
          UTC: string;
          [key: string]: string | number | null | undefined;
        };

        // Group dataframe by measurement series and files
        const seriesData = new Map<string, Array<DataSeriesPoint>>();

        // Process each data point
        dataframe.forEach((point: DataPoint) => {
          const { filename, UTC, ...measurements } = point;
          if (!filename || !UTC || typeof filename !== "string") {
            return;
          }

          const measurementType = fileToMeasurement.get(filename);
          if (!measurementType || typeof measurementType !== "string") {
            return;
          }

          // Get value for this measurement type
          const value = measurements[measurementType];

          // Create series for this file if doesn't exist
          if (!seriesData.has(filename)) {
            seriesData.set(filename, []);
          }

          // Add data point
          seriesData.get(filename)?.push({
            UTC,
            [measurementType]: value,
          });
        });

        // Convert to array matching info_df order
        const processedFrames = info_df.map((info: InfoType) => {
          const filename = info["Name der Datei"];
          const data = seriesData.get(filename) || [];
          return data;
        });

        // Set data if valid series exist
        if (processedFrames.some((series: DataSeriesPoint[]) => series && series.length > 0)) {
          setProcessedDataFrames(processedFrames);
          setAdjustedData(info_df);
        } else {
          throw new Error(t('errors.noValidData'));
        }
      }

      return false; // Processing complete, no methods required
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('errors.processingFailed');
      setUploadError(errorMessage);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [t]);

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setIsProcessing(false);
    setUploadProgress(null);
    setUploadError(null);
    setAdjustedData([]);
    setProcessedDataFrames([]);
  }, []);

  return {
    isProcessing,
    uploadProgress,
    uploadError,
    adjustedData,
    processedDataFrames,
    processData,
    resetUpload
  };
};
